/**
 * Automatic Memory Leak Detector
 *
 * This tool automatically:
 * 1. Monitors memory growth patterns
 * 2. Detects potential leaks
 * 3. Takes automatic "snapshots" of cache sizes
 * 4. Logs detailed reports
 * 5. Alerts when leaks are detected
 */

import './profiling.types';

interface LeakReport {
  timestamp: Date;
  memoryMB: number;
  reactQueryCacheSize: number;
  bodyCacheSize: number;
  bodyCacheSizeMB: number;
  eventListenerCount: number;
  isLeaking: boolean;
  leakSources: string[];
}

class AutoLeakDetector {
  private reports: LeakReport[] = [];
  private intervalId: number | null = null;
  private startMemory = 0;
  private alertThresholdMB = 500; // Alert if memory grows by 500MB
  private checkIntervalMs = 60000; // Check every minute

  /**
   * Start automatic leak detection
   */
  start(): void {
    // Skip if not in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    if (this.intervalId) {
      console.warn('[AutoLeakDetector] Already running');
      return;
    }

    console.log('🔍 [AutoLeakDetector] Starting automatic leak detection...');

    // Capture baseline
    this.startMemory = this.getMemoryMB();
    console.log(
      `📊 [AutoLeakDetector] Baseline memory: ${this.startMemory.toFixed(2)} MB`,
    );

    // Check every minute
    this.intervalId = window.setInterval(() => {
      this.check();
    }, this.checkIntervalMs);

    // Initial check
    setTimeout(() => this.check(), 5000);
  }

  /**
   * Stop detection
   */
  stop(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[AutoLeakDetector] Stopped');
    }
  }

  /**
   * Perform leak check
   */
  private async check(): Promise<void> {
    const report = await this.generateReport();
    this.reports.push(report);

    // Keep only last 100 reports
    if (this.reports.length > 100) {
      this.reports.shift();
    }

    // Log report
    this.logReport(report);

    // Check for leaks
    if (report.isLeaking) {
      this.alertLeak(report);
    }
  }

  /**
   * Generate comprehensive report
   */
  private async generateReport(): Promise<LeakReport> {
    const memoryMB = this.getMemoryMB();
    const reactQueryCacheSize = this.getReactQueryCacheSize();
    const { size: bodyCacheSize, sizeMB: bodyCacheSizeMB } =
      this.getBodyCacheInfo();
    const eventListenerCount = this.getEventListenerCount();

    // Detect leak patterns
    const leakSources: string[] = [];
    let isLeaking = false;

    // Check 1: Memory growth
    const memoryGrowth = memoryMB - this.startMemory;
    if (memoryGrowth > this.alertThresholdMB) {
      isLeaking = true;
      leakSources.push(`Memory grew by ${memoryGrowth.toFixed(2)} MB`);
    }

    // Check 2: React Query cache size
    if (reactQueryCacheSize > 200) {
      isLeaking = true;
      leakSources.push(`React Query cache has ${reactQueryCacheSize} queries`);
    }

    // Check 3: Body cache size
    if (bodyCacheSizeMB > 500) {
      isLeaking = true;
      leakSources.push(`Body cache is ${bodyCacheSizeMB.toFixed(2)} MB`);
    }

    // Check 4: Event listeners
    if (eventListenerCount > 50) {
      isLeaking = true;
      leakSources.push(`${eventListenerCount} event listeners registered`);
    }

    // Check 5: Memory trend (last 5 reports)
    if (this.reports.length >= 5) {
      const recent = this.reports.slice(-5);
      const trend = recent[recent.length - 1].memoryMB - recent[0].memoryMB;
      if (trend > 200) {
        isLeaking = true;
        leakSources.push(
          `Memory growing steadily (+${trend.toFixed(2)} MB in 5 min)`,
        );
      }
    }

    return {
      timestamp: new Date(),
      memoryMB,
      reactQueryCacheSize,
      bodyCacheSize,
      bodyCacheSizeMB,
      eventListenerCount,
      isLeaking,
      leakSources,
    };
  }

  /**
   * Log report to console
   */
  private logReport(report: LeakReport): void {
    const icon = report.isLeaking ? '🔴' : '✅';
    const time = report.timestamp.toLocaleTimeString();

    console.group(`${icon} [AutoLeakDetector] ${time}`);
    console.log(`Memory: ${report.memoryMB.toFixed(2)} MB`);
    console.log(`React Query Cache: ${report.reactQueryCacheSize} queries`);
    console.log(
      `Body Cache: ${report.bodyCacheSize} items (${report.bodyCacheSizeMB.toFixed(2)} MB)`,
    );
    console.log(`Event Listeners: ${report.eventListenerCount}`);

    if (report.isLeaking) {
      console.warn('⚠️ LEAK DETECTED:');
      report.leakSources.forEach((source) => console.warn(`  - ${source}`));
    }

    console.groupEnd();
  }

  /**
   * Alert about leak
   */
  private alertLeak(report: LeakReport): void {
    console.error('═══════════════════════════════════════════════════════');
    console.error('🔴 MEMORY LEAK DETECTED 🔴');
    console.error('═══════════════════════════════════════════════════════');
    console.error(`Time: ${report.timestamp.toLocaleString()}`);
    console.error(`Memory: ${report.memoryMB.toFixed(2)} MB`);
    console.error(
      `Growth: ${(report.memoryMB - this.startMemory).toFixed(2)} MB from baseline`,
    );
    console.error('');
    console.error('Leak Sources:');
    report.leakSources.forEach((source) => console.error(`  • ${source}`));
    console.error('');
    console.error('Recommendations:');
    if (report.reactQueryCacheSize > 200) {
      console.error('  → Add gcTime to React Query config');
    }
    if (report.bodyCacheSizeMB > 500) {
      console.error('  → Implement size-based eviction in body cache');
    }
    if (report.eventListenerCount > 50) {
      console.error('  → Fix EventEmitter cleanup in SSE provider');
    }
    console.error('═══════════════════════════════════════════════════════');
  }

  /**
   * Get current memory usage in MB
   */
  private getMemoryMB(): number {
    if (typeof performance !== 'undefined' && performance.memory) {
      return performance.memory.usedJSHeapSize / (1024 * 1024);
    }
    return 0;
  }

  /**
   * Get React Query cache size
   */
  private getReactQueryCacheSize(): number {
    try {
      const queryClient = window.queryClient;
      if (queryClient?.getQueryCache) {
        return queryClient.getQueryCache().getAll().length;
      }
    } catch {
      // Silent fail
    }
    return 0;
  }

  /**
   * Get body cache info
   */
  private getBodyCacheInfo(): { size: number; sizeMB: number } {
    try {
      const bodyCache = window.bodyCache;
      if (bodyCache instanceof Map) {
        let totalBytes = 0;
        for (const body of bodyCache.values()) {
          totalBytes +=
            (body.requestBody?.length || 0) + (body.responseBody?.length || 0);
        }
        return {
          size: bodyCache.size,
          sizeMB: totalBytes / (1024 * 1024),
        };
      }
    } catch {
      // Silent fail
    }
    return { size: 0, sizeMB: 0 };
  }

  /**
   * Get event listener count
   */
  private getEventListenerCount(): number {
    try {
      const eventEmitter = window.eventEmitter;
      if (eventEmitter?.events) {
        let count = 0;
        for (const listeners of Object.values(eventEmitter.events)) {
          if (Array.isArray(listeners)) {
            count += listeners.length;
          }
        }
        return count;
      }
    } catch {
      // Silent fail
    }
    return 0;
  }

  /**
   * Get full report
   */
  getFullReport(): string {
    if (this.reports.length === 0) {
      return 'No data collected yet. Wait a few minutes.';
    }

    const latest = this.reports[this.reports.length - 1];
    const memoryGrowth = latest.memoryMB - this.startMemory;
    const avgMemory =
      this.reports.reduce((sum, r) => sum + r.memoryMB, 0) /
      this.reports.length;

    return `
╔════════════════════════════════════════════════════════════╗
║           AUTOMATIC LEAK DETECTION REPORT                  ║
╠════════════════════════════════════════════════════════════╣
║ Duration:           ${((this.reports.length * this.checkIntervalMs) / 60000).toFixed(1)} minutes
║ Checks Performed:   ${this.reports.length}
║ Leaks Detected:     ${this.reports.filter((r) => r.isLeaking).length}
║ 
║ MEMORY:
║   Baseline:         ${this.startMemory.toFixed(2)} MB
║   Current:          ${latest.memoryMB.toFixed(2)} MB
║   Growth:           ${memoryGrowth > 0 ? '+' : ''}${memoryGrowth.toFixed(2)} MB
║   Average:          ${avgMemory.toFixed(2)} MB
║ 
║ CACHES:
║   React Query:      ${latest.reactQueryCacheSize} queries
║   Body Cache:       ${latest.bodyCacheSize} items (${latest.bodyCacheSizeMB.toFixed(2)} MB)
║   Event Listeners:  ${latest.eventListenerCount}
║ 
║ STATUS:             ${latest.isLeaking ? '🔴 LEAKING' : '✅ HEALTHY'}
${latest.isLeaking ? '║ \n║ LEAK SOURCES:\n' + latest.leakSources.map((s) => `║   • ${s}`).join('\n') : ''}
╚════════════════════════════════════════════════════════════╝`;
  }

  /**
   * Export all reports as CSV
   */
  exportCSV(): string {
    const headers =
      'Timestamp,Memory MB,Memory Growth MB,React Query Cache,Body Cache Items,Body Cache MB,Event Listeners,Is Leaking,Leak Sources\n';
    const rows = this.reports
      .map((r) => {
        const growth = r.memoryMB - this.startMemory;
        return `${r.timestamp.toISOString()},${r.memoryMB},${growth},${r.reactQueryCacheSize},${r.bodyCacheSize},${r.bodyCacheSizeMB},${r.eventListenerCount},${r.isLeaking},"${r.leakSources.join('; ')}"`;
      })
      .join('\n');

    return headers + rows;
  }

  /**
   * Get reports
   */
  getReports(): LeakReport[] {
    return [...this.reports];
  }
}

// Singleton instance
export const autoLeakDetector = new AutoLeakDetector();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.autoLeakDetector = autoLeakDetector;
}

// Auto-start in development (browser only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  setTimeout(() => {
    autoLeakDetector.start();
    console.log(
      '💡 Tip: Run autoLeakDetector.getFullReport() in console for summary',
    );
  }, 15000); // Start after 15 seconds
}
